import { Inject, Injectable, Optional } from '@angular/core';
import {
    assertUnreachable,
    ConfigConstants,
    isDefined,
    ModalClass,
    YesNoModalDialogComponent,
    YesNoModalDialogModel,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { combineLatest, defer, EMPTY, from, of } from 'rxjs';
import {
    catchError,
    concatMap,
    distinctUntilChanged,
    filter,
    map,
    mapTo,
    mergeMap,
    switchMap,
    take,
    withLatestFrom,
} from 'rxjs/operators';
import { ConfirmationInstanceToken, ConfirmationInterface } from '../../core/interfaces/confirmation.interface';
import { XmlParserService } from '../../services/xml-parser.service';
import { XmlSerializerService } from '../../services/xml-serializer.service';
import { CHOICE_TYPE } from '../../types/choice';
import { DatePerformed, isDatePerformed } from '../../types/date-performed';
import { FormsConstants } from '../../types/forms-constants';
import { isCosimaOption } from '../../types/option';
import { RADIO_OPTION_TYPE } from '../../types/radio-option';
import {
    isReplacementDateOrLastProcedureDate,
    REPLACEMENT_OR_LAST_PROCEDURE_DATE_SELECTION,
} from '../../types/replacement-or-last-procedure-date';
import { DEFICIENCY_SELECTION, MEASUREMENT_SELECTION } from '../../types/system-condition';
import { DateUtilities } from '../../utilities/date-utilities';
import { FormUtilities } from '../../utilities/form-utilities';
import { getXmlIdsFromFunctionalElement } from '../../utilities/get-xml-ids-from-functional-element';
import { autofillCheckpoints, updateCheckpoint, updateCheckpoints } from '../actions/checkpoint.actions';
import { updateCustomerIdentification } from '../actions/customer-identification.actions';
import { emptyAction } from '../actions/empty.actions';
import {
    enableManualIndividualization,
    markFormAsEdited,
    updatedXml,
    updateSignFieldSuccess,
    updateTopicVisibility,
} from '../actions/form.actions';
import {
    autofillFunctionalElements,
    updateAstoriaOption,
    updateCosimaOption,
    updateDatePerformed,
    updateDescription,
    updateDiscrepancyLog,
    updateDropDownOptions,
    updateFunctionalElementRemarks,
    updateFunctionalElementsChoicesSelection,
    updateFunctionalElementSelection,
    updateMaterialNumber,
    updateMaterialSerialNumber,
    updateMaterialSwapIn,
    updateMaterialSwapOut,
    updateOptionSet,
    updateRemarksSection,
    updateRepairTime,
    updateReplacementOrLastProcedureDateSelection,
    updateReplacementOrLastProcedureDateValue,
    updateSerialNumber,
    updateSerialSwapIn,
    updateSerialSwapOut,
    updateSignature,
    updateSignatureName,
    updateSignatureSuccess,
    updateSystemConditionSignature,
    updateSystemConditionSignatureDisabled,
    updateSystemConditionSignatureName,
    updateValue,
} from '../actions/functional-elements.actions';
import { updateDefectServicePartIdentificationField } from '../actions/questionnaire.actions';
import {
    updateSystemConditionDeficienciesSelection,
    updateSystemConditionDeficiency,
    updateSystemConditionMeasurementsSelection,
    updateSystemConditionRemarks,
} from '../actions/system-condition.actions';
import {
    getCheckpointsToAutofillFromContentFactory,
    getFormCheckPointDependentFunctionElementByIdFactory,
    getFormContent,
    getFormCustomerSignatureForCurrentParticipant,
    getFormDoctype,
    getFormFunctionalElementByIdFactory,
    getFormFunctionalElements,
    getFormNotification,
    getFormOptionDependenciesByIdFactory,
    getFormRadioOptions,
    getFormSelectedParticipantCopy,
    getFormSelectedParticipantCopyId,
    getFormSystemCondition,
    getFormType,
    getFunctionalElementsToAutofillFromContentFactory,
    getMeasuringEquipments,
} from '../selectors/form.selector';
import { FormsState } from '../state/pace-forms.state';
import { DropDownOptionsFunctionalElement, FunctionTypes } from '../../types/drop-down-options';
import { ContentBlock } from '../../types/content-block';
import { isRemarksFunctionalElement } from '../../types/standard-functional-element';
import { isMaterialSerialNumber } from '../../types/material-serial-number';
import { isValue } from '../../types/value-functional-element';
import {
    disableMeasurementEquipmentTable,
    updateMeasurementEquipmentColumnData,
    updateMeasurementEquipmentRowVisibility,
    updateMeasurementEquipmentSubmitSatus,
    updateMeasurementEquipmentsVisibility,
} from '../actions/measurement-equipments.actions';
import { FUNCTIONAL_ELEMENT_TYPE } from '../../types/functional-element';

// Effects for editing the form
@Injectable()
export class FormEditEffects {
    public constructor(
        private readonly actions$: Actions,
        private readonly store: Store<FormsState>,
        private readonly xmlParserService: XmlParserService,
        private readonly xmlSerializerService: XmlSerializerService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Optional() @Inject(ConfirmationInstanceToken) private readonly confirmationService: ConfirmationInterface,
        private readonly modalService: BsModalService,
        private readonly translate: TranslateService,
    ) {}

    public markFormAsEdited$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                autofillFunctionalElements,
                autofillCheckpoints,
                updateFunctionalElementSelection,
                updateDiscrepancyLog,
                updateSignature,
                updateSignatureName,
                updateSystemConditionSignatureName,
                updateSystemConditionSignature,
                updateSystemConditionSignatureDisabled,
                updateMaterialNumber,
                updateSerialNumber,
                updateDescription,
                updateMaterialSerialNumber,
                updateDatePerformed,
                updateReplacementOrLastProcedureDateSelection,
                updateReplacementOrLastProcedureDateValue,
                updateValue,
                updateAstoriaOption,
                updateCosimaOption,
                updateFunctionalElementRemarks,
                updateMaterialSwapIn,
                updateSerialSwapIn,
                updateMaterialSwapOut,
                updateSerialSwapOut,
                updateRepairTime,
                updateDefectServicePartIdentificationField,
                updateRemarksSection,
                updateMeasurementEquipmentColumnData,
                disableMeasurementEquipmentTable,
                updateDropDownOptions,
                updateMeasurementEquipmentRowVisibility,
                updateSystemConditionMeasurementsSelection,
                updateSystemConditionDeficiency,
                updateSystemConditionRemarks,
                updateSystemConditionDeficienciesSelection,
            ),
            mapTo(markFormAsEdited()),
        ),
    );

    public updateValueField$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateValue),
            withLatestFrom(
                this.store.select(getFormSelectedParticipantCopyId),
                this.store.select(getFormFunctionalElements),
                this.store.select(getFormDoctype),
                this.store.select(getFormType),
            ),
            map(([action, selectedParticipantId, functionalElements, doctype, formType]) => {
                // Upda te value in XML parser
                if (FormUtilities.isAstoriaForm(formType)) {
                    this.xmlSerializerService.updateInputField(action.xmlId, action.newValue, formType);
                } else if (FormUtilities.isCosimaForm(formType)) {
                    // upda te the choices inside the XML for cosima
                    const functionalElement = functionalElements[action.id];
                    // we a lways have choices for cosima
                    const choices = functionalElement.choices;
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore --- @TEO can you please check that?
                    const selectedChoice = Object.values(choices).find((choice) => choice?.selected);
                    const remarksHidden = FormUtilities.isRemarksFieldHidden(functionalElement);

                    this.xmlSerializerService.updateElementVisibility(functionalElement.remarksXmlId, !remarksHidden);

                    if (functionalElement.xmlId) {
                        this.xmlSerializerService.updateFunctionalElementSelection(
                            functionalElement.xmlId,
                            isDefined(selectedChoice) ? selectedChoice.xmlId : undefined,
                        );
                    }

                    if (action.valueXmlId) {
                        this.xmlSerializerService.updateInputField(action.valueXmlId, action.newValue, formType);
                    }
                } else {
                    throw new Error(`FormEditEffects: updateValueField$: unknown form type: ${formType}`);
                }
                return updatedXml({
                    participant: selectedParticipantId,
                    xml: this.xmlParserService.getXml(doctype),
                });
            }),
            catchError((error) => {
                this.log.error(`FormEditEffects: updateValueField$: error in pipeline: ${JSON.stringify(error)}`);
                return EMPTY;
            }),
        ),
    );

    // generic effect to update the XML of any input field where the action contains its xmlId and the new value
    public updateInputField$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                updateCustomerIdentification,
                updateDiscrepancyLog,
                updateMaterialNumber,
                updateSerialNumber,
                updateDescription,
                updateSystemConditionRemarks,
                updateFunctionalElementRemarks,
                updateMaterialSwapIn,
                updateSerialSwapIn,
                updateMaterialSwapOut,
                updateSerialSwapOut,
                updateRepairTime,
                updateRemarksSection,
            ),
            withLatestFrom(this.store.select(getFormDoctype), this.store.select(getFormType)),
            switchMap(([action, doctype, formType]) =>
                this.store.select(getFormSelectedParticipantCopyId).pipe(
                    map((selectedParticipantId) => {
                        // Upda te value in XML parser
                        this.xmlSerializerService.updateInputField(action.xmlId, action.newValue, formType);
                        return updatedXml({
                            participant: selectedParticipantId,
                            xml: this.xmlParserService.getXml(doctype),
                        });
                    }),
                    catchError((error) => {
                        this.log.error(
                            `FormEditEffects: updateInputField$: error in pipeline: ${JSON.stringify(error)}`,
                        );
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );

    public updateFunctionalElementSelection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateFunctionalElementSelection),
            switchMap((action) =>
                combineLatest([
                    this.store.select(getFormSelectedParticipantCopyId),
                    this.store.select(getFormFunctionalElements),
                    this.store.select(getFormDoctype),
                ]).pipe(
                    take(1),
                    map(([selectedParticipantId, functionalElements, doctype]) => {
                        const functionalElement = functionalElements[action.id];
                        const xmlId = functionalElement.xmlId;
                        if (!isDefined(xmlId)) {
                            throw new Error(
                                `FormEditEffects: updateFunctionalElementSelection$: encountered functional element without XML id!`,
                            );
                        }
                        // Upda te value in XML parser
                        this.xmlSerializerService.updateFunctionalElementSelection(xmlId, action.selectedChoiceXmlId);
                        return updatedXml({
                            participant: selectedParticipantId,
                            xml: this.xmlParserService.getXml(doctype),
                        });
                    }),
                    catchError((error) => {
                        this.log.error(
                            `FormEditEffects: updateFunctionalElementSelection$: error in pipeline: ${JSON.stringify(
                                error,
                            )}`,
                        );
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );

    public updateMaterialSerialNumber$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateMaterialSerialNumber),
            switchMap((action) =>
                defer(() => {
                    const actions: Array<
                        | ReturnType<typeof updateMaterialNumber>
                        | ReturnType<typeof updateSerialNumber>
                        | ReturnType<typeof updateDescription>
                    > = [
                        updateMaterialNumber({
                            id: action.id,
                            newValue: action.materialValue,
                            xmlId: action.materialXmlId,
                        }),
                        updateSerialNumber({
                            id: action.id,
                            newValue: action.serialValue,
                            xmlId: action.serialXmlId,
                        }),
                    ];

                    // Only for Astoria!
                    // If the functional element which we want to update is a MATERIAL_SERIAL_NO_WITH_DESC, then it will
                    // have a description xmlId from the XML. Only then do we emit the respective action which will update
                    // both the state in the reducer and the XML by an effect.
                    if (action.descriptionXmlId.length > 0) {
                        actions.push(
                            updateDescription({
                                id: action.id,
                                newValue: action.descriptionValue,
                                xmlId: action.descriptionXmlId,
                            }),
                        );
                    }

                    return actions;
                }).pipe(
                    catchError((error) => {
                        this.log.error(
                            `FormEditEffects: updateMaterialSerialNumber$: error in pipeline: ${JSON.stringify(error)}`,
                        );
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );

    public updateSignatureName$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateSignatureName, updateSystemConditionSignatureName),
            withLatestFrom(this.store.select(getFormDoctype), this.store.select(getFormType)),
            switchMap(([action, doctype, formType]) =>
                this.store.select(getFormSelectedParticipantCopyId).pipe(
                    map((selectedParticipantId) => {
                        // Upda te value in XML parser
                        this.xmlSerializerService.updateInputField(
                            action.xmlId,
                            action.firstName === FormsConstants.NA && action.lastName === FormsConstants.NA
                                ? FormsConstants.NA
                                : `${action.firstName} ${action.lastName}`,
                            formType,
                        );
                        return updatedXml({
                            participant: selectedParticipantId,
                            xml: this.xmlParserService.getXml(doctype),
                        });
                    }),
                    catchError((error) => {
                        this.log.error(
                            `FormEditEffects: updateSignatureName$: error in pipeline: ${JSON.stringify(error)}`,
                        );
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );

    public updateSystemConditionDeficiency$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateSystemConditionDeficiency),
            switchMap((action) =>
                combineLatest([
                    this.store.select(getFormSelectedParticipantCopyId),
                    this.store.select(getFormSystemCondition),
                    this.store.select(getFormDoctype),
                ]).pipe(
                    take(1),
                    map(([selectedParticipantId, systemCondition, doctype]) => {
                        const xmlId = systemCondition?.choicesFunctionalElementXmlId;
                        if (!isDefined(xmlId)) {
                            throw new Error(
                                `FormEditEffects: updateSystemConditionDeficiency$: encountered functional element without XML id!`,
                            );
                        }
                        // Upda te value in XML parser
                        this.xmlSerializerService.updateFunctionalElementSelection(xmlId, action.xmlId);
                        return updatedXml({
                            participant: selectedParticipantId,
                            xml: this.xmlParserService.getXml(doctype),
                        });
                    }),
                    catchError((error) => {
                        this.log.error(
                            `FormEditEffects: updateSystemConditionDeficiency$: error in pipeline: ${JSON.stringify(
                                error,
                                Object.getOwnPropertyNames(error),
                            )}`,
                        );
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );

    public updateSignature$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateSignature, updateSystemConditionSignature),
            switchMap((action) =>
                combineLatest([
                    this.store.select(getFormSelectedParticipantCopyId),
                    this.store
                        .select(getFormType)
                        .pipe(filter((formType) => formType !== FormsConstants.FORMTYPE.UNKNOWN)),
                    this.store.select(getFormDoctype),
                ]).pipe(
                    mergeMap(([selectedParticipantId, formType, doctype]) => {
                        this.xmlSerializerService.updateInputField(action.dateXmlId, action.date, formType);

                        return from([
                            updatedXml({
                                participant: selectedParticipantId,
                                xml: this.xmlParserService.getXml(doctype),
                            }),
                            updateSignatureSuccess(),
                        ]);
                    }),
                    catchError((error) => {
                        this.log.error(
                            `FormEditEffects: updateSignature$: error in pipeline: ${JSON.stringify(error)}`,
                        );
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );

    public updateSignField$ = createEffect(() =>
        this.actions$.pipe(
            // Only necessary for regular signatures (as opposed to system condition signatures) since those are the
            // only ones supported by a participant copy workflow that also needs to update attendees.
            ofType(updateSignature, updateSignatureName),
            switchMap((action) =>
                combineLatest([
                    this.store.select(getFormNotification),
                    this.store.select(getFormSelectedParticipantCopy),
                    this.store.select(getFormCustomerSignatureForCurrentParticipant),
                ]).pipe(
                    mergeMap(([notification, participantCopy, customerSignature]) => {
                        if (
                            typeof participantCopy !== 'undefined' &&
                            typeof customerSignature !== 'undefined' &&
                            action.id === customerSignature.id &&
                            participantCopy.id !== FormsConstants.MASTER_PARTICIPANT_ID
                        ) {
                            return this.confirmationService.saveAttendee(notification, participantCopy);
                        } else {
                            return of(undefined);
                        }
                    }),
                    map(() => updateSignFieldSuccess()),
                    catchError((error) => {
                        this.log.error(
                            `FormEditEffects: updateSignField$: error in pipeline: ${JSON.stringify(error)}`,
                        );
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );

    public updateDateField$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateDatePerformed, updateReplacementOrLastProcedureDateValue),
            switchMap((action) =>
                combineLatest([
                    this.store.select(getFormSelectedParticipantCopyId),
                    this.store.select(getFormDoctype),
                    this.store.select(getFormType),
                    this.store
                        .select(getFormFunctionalElementByIdFactory<DatePerformed>({ id: action.id }))
                        .pipe(filter(isDefined)),
                ]).pipe(
                    take(1),
                    map(([selectedParticipantId, doctype, formType, datePerformed]) => {
                        // Upda te value in XML parser
                        const newDate =
                            action.newValue.length > 0 && action.newValue !== FormsConstants.NA
                                ? moment(action.newValue)
                                : moment.invalid();

                        if (FormUtilities.isAstoriaForm(formType)) {
                            const { day, month, year } = DateUtilities.getDayMonthYearValuesFromDateString(
                                action.newValue,
                            );

                            if (action.dayXmlId) {
                                this.xmlSerializerService.updateInputField(action.dayXmlId, day, formType);
                            }
                            if (action.monthXmlId) {
                                this.xmlSerializerService.updateInputField(action.monthXmlId, month, formType);
                            }
                            if (action.yearXmlId) {
                                this.xmlSerializerService.updateInputField(action.yearXmlId, year, formType);
                            }
                        } else if (FormUtilities.isCosimaForm(formType)) {
                            const isChoiceNaSelected = datePerformed.choices?.choiceNa?.selected;

                            const previousLocale = moment.locale();
                            // set the locale so that we can always parse the date in english
                            moment.locale('en');
                            const newDateValue = newDate.isValid()
                                ? newDate.format(ConfigConstants.formatPatternDateCosima)
                                : // the date is invalid if the user resets the date, or when the user selects N.A.
                                  // we need to figure out, if the selected choice is NA then we need to set it to N.A
                                  // otherwise reset it to an empty string.
                                  isChoiceNaSelected
                                  ? FormsConstants.NA
                                  : '';

                            // restore the locale to the previous one, as we need to set it to english just for saving the date in the XML
                            moment.locale(previousLocale);
                            if (action.dateXmlId) {
                                this.xmlSerializerService.updateInputField(action.dateXmlId, newDateValue, formType);
                            }
                        } else {
                            throw new Error(`FormEditEffects: updateDateField$: unknown form type: ${formType}`);
                        }

                        return updatedXml({
                            participant: selectedParticipantId,
                            xml: this.xmlParserService.getXml(doctype),
                        });
                    }),
                    catchError((error) => {
                        this.log.error(`FormEditEffects: updateDateField$: error in pipeline: ${error}`);
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );

    public updateReplacementDateSelection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateReplacementOrLastProcedureDateSelection),
            switchMap((action) =>
                combineLatest([
                    this.store.select(getFormSelectedParticipantCopyId),
                    this.store.select(getFormDoctype),
                ]).pipe(
                    map(([selectedParticipantId, doctype]) => {
                        let selection: RADIO_OPTION_TYPE | undefined;
                        switch (action.selected) {
                            case REPLACEMENT_OR_LAST_PROCEDURE_DATE_SELECTION.NONE:
                                selection = undefined;
                                break;
                            case REPLACEMENT_OR_LAST_PROCEDURE_DATE_SELECTION.STARTUP_DATE:
                                selection = RADIO_OPTION_TYPE.YES;
                                break;
                            case REPLACEMENT_OR_LAST_PROCEDURE_DATE_SELECTION.OTHER_DATE:
                                selection = RADIO_OPTION_TYPE.NO;
                                break;
                            default:
                                assertUnreachable(action.selected);
                        }
                        this.xmlSerializerService.updateCheckpoint(action.xmlId, selection);
                        return updatedXml({
                            participant: selectedParticipantId,
                            xml: this.xmlParserService.getXml(doctype),
                        });
                    }),
                    catchError((error) => {
                        this.log.error(`FormEditEffects: updateReplacementDateSelection$: error in pipeline: ${error}`);
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );

    public updateOption$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateAstoriaOption, updateCosimaOption),
            switchMap((action) =>
                combineLatest([
                    this.store.select(getFormSelectedParticipantCopyId),
                    this.store.select(getFormDoctype),
                    this.store.select(getFormFunctionalElements),
                    this.store.select(getFormOptionDependenciesByIdFactory({ contentId: action.id })),
                ]).pipe(
                    take(1),
                    map(([selectedParticipantId, doctype, functionalElements, dependencies]) => {
                        this.xmlSerializerService.updateOption(action, functionalElements);

                        this.xmlSerializerService.updateDependenciesForOption(action, dependencies, functionalElements);

                        const optionPresentElement = functionalElements[action.id];
                        if (isCosimaOption(optionPresentElement)) {
                            const selectedChoice = Object.values(optionPresentElement.choices).find(
                                (choice) => choice?.selected,
                            );
                            if (selectedChoice?.type === CHOICE_TYPE.CHOICE_NEGATIVE) {
                                const sibling = FormUtilities.getNextOptionPresentSibling(
                                    optionPresentElement,
                                    functionalElements,
                                );

                                if (isDefined(sibling)) {
                                    return updateCosimaOption({
                                        id: sibling.id,
                                        selectedChoiceXmlId: sibling.choices?.choiceNegative?.xmlId,
                                    });
                                }
                            }
                        }

                        return updatedXml({
                            participant: selectedParticipantId,
                            xml: this.xmlParserService.getXml(doctype),
                        });
                    }),
                    catchError((error) => {
                        this.log.error(`FormEditEffects: updateOption$: error in pipeline: ${error}`);
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );

    public updateCheckpoint$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateCheckpoint),
            switchMap((action) =>
                combineLatest([
                    this.store.select(getFormSelectedParticipantCopyId),
                    this.store.select(getFormDoctype),
                    this.store.select(
                        getFormCheckPointDependentFunctionElementByIdFactory({
                            xmlId: action.xmlId,
                        }),
                    ),
                    this.store.select(getFormType),
                ]).pipe(
                    map(([selectedParticipantId, doctype, depFunctionalElement, formType]) => {
                        this.xmlSerializerService.updateCheckpoint(action.xmlId, action.selected);

                        // if t here is dependent functional element, then only execute next logic else not needed
                        // impo rtant for dependent function element of type: MaterialSerialNumber, Value, ValueUnit
                        if (isDefined(depFunctionalElement)) {
                            if (
                                isReplacementDateOrLastProcedureDate(depFunctionalElement) &&
                                depFunctionalElement.date.length === 0
                            ) {
                                // if t he functional element doesn't have any date set( no date selected by the user)
                                // and  it's controlling checkpoint isw set to another value than N.A then set in the XML the values
                                // DD f or day, MMM for month and YYYY for year
                                // If t he selected value in the controlling checkpoint is set to N.A then the date of the functional
                                // elem ent will be set to N.A. - hence the check of the date length is enough.
                                this.xmlSerializerService.updateInputField(
                                    depFunctionalElement.dayXmlId,
                                    'DD',
                                    formType,
                                );
                                this.xmlSerializerService.updateInputField(
                                    depFunctionalElement.monthXmlId,
                                    'MMM',
                                    formType,
                                );
                                this.xmlSerializerService.updateInputField(
                                    depFunctionalElement.yearXmlId,
                                    'YYYY',
                                    formType,
                                );
                            } else {
                                getXmlIdsFromFunctionalElement(depFunctionalElement).forEach(({ xmlId, value }) => {
                                    if (xmlId) {
                                        this.xmlSerializerService.updateInputField(xmlId, value, formType);
                                    }
                                });
                            }
                        }

                        return updatedXml({
                            participant: selectedParticipantId,
                            xml: this.xmlParserService.getXml(doctype),
                        });
                    }),
                    catchError((error) => {
                        this.log.error(`FormEditEffects: updateCheckpoint$: error in pipeline: ${error}`);
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );

    public updateCheckpoints$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateCheckpoints),
            withLatestFrom(this.store.select(getFormDoctype)),
            switchMap(([action, doctype]) =>
                this.store.select(getFormSelectedParticipantCopyId).pipe(
                    map((selectedParticipantId) => {
                        action.xmlIds.forEach((xmlId) =>
                            this.xmlSerializerService.updateCheckpoint(xmlId, action.selected),
                        );
                        return updatedXml({
                            participant: selectedParticipantId,
                            xml: this.xmlParserService.getXml(doctype),
                        });
                    }),
                    catchError((error) => {
                        this.log.error(`FormEditEffects: updateCheckpoints$: error in pipeline: ${error}`);
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );

    public updateSystemConditionDeficienciesSelection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateSystemConditionDeficienciesSelection),
            withLatestFrom(this.store.select(getFormDoctype)),
            switchMap(([action, doctype]) =>
                this.store.select(getFormSelectedParticipantCopyId).pipe(
                    map((selectedParticipantId) => {
                        let selection: RADIO_OPTION_TYPE | undefined;
                        switch (action.selected) {
                            case DEFICIENCY_SELECTION.NO_SELECTION:
                                selection = undefined;
                                break;
                            case DEFICIENCY_SELECTION.NO_DEFICIENCIES:
                                selection = RADIO_OPTION_TYPE.YES;
                                break;
                            case DEFICIENCY_SELECTION.SLIGHT_DEFICIENCIES:
                                selection = RADIO_OPTION_TYPE.NO;
                                break;
                            case DEFICIENCY_SELECTION.SERIOUS_DEFICIENCIES:
                                selection = RADIO_OPTION_TYPE.NA;
                                break;
                            default:
                                assertUnreachable(action.selected);
                        }
                        this.xmlSerializerService.updateCheckpoint(action.xmlId, selection);
                        return updatedXml({
                            participant: selectedParticipantId,
                            xml: this.xmlParserService.getXml(doctype),
                        });
                    }),
                    catchError((error) => {
                        this.log.error(
                            `FormEditEffects: updateSystemConditionDeficienciesSelection$: error in pipeline: ${error}`,
                        );
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );

    public updateSystemConditionMeasurementSelection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateSystemConditionMeasurementsSelection),
            withLatestFrom(this.store.select(getFormDoctype)),
            switchMap(([action, doctype]) =>
                this.store.select(getFormSelectedParticipantCopyId).pipe(
                    map((selectedParticipantId) => {
                        let selection: RADIO_OPTION_TYPE | undefined;
                        switch (action.selected) {
                            case MEASUREMENT_SELECTION.NO_SELECTION:
                                selection = undefined;
                                break;
                            case MEASUREMENT_SELECTION.INITIAL_MEASUREMENT:
                                selection = RADIO_OPTION_TYPE.YES;
                                break;
                            case MEASUREMENT_SELECTION.REPEAT_MEASUREMENT:
                                selection = RADIO_OPTION_TYPE.NO;
                                break;
                            default:
                                assertUnreachable(action.selected);
                        }
                        this.xmlSerializerService.updateCheckpoint(action.xmlId, selection);
                        return updatedXml({
                            participant: selectedParticipantId,
                            xml: this.xmlParserService.getXml(doctype),
                        });
                    }),
                    catchError((error) => {
                        this.log.error(
                            `FormEditEffects: updateSystemConditionMeasurementSelection$: error in pipeline: ${error}`,
                        );
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );

    public autofillCheckpoints$ = createEffect(() =>
        this.actions$.pipe(
            ofType(autofillCheckpoints),
            switchMap((action) =>
                combineLatest([
                    this.store.select(getCheckpointsToAutofillFromContentFactory({ contentId: action.id })),
                    this.store.select(getFormRadioOptions),
                ]).pipe(
                    take(1),
                    mergeMap(([checkpoints, options]) => {
                        // Auto fill only when there was no selection for the checkpoint in the XML
                        const hasCheckpointSelectionsFromXml = checkpoints.some((checkpoint) => {
                            const selectedOption = options[checkpoint.xmlId]?.find((o) => o.selected);
                            return selectedOption?.contentLoadedFromXml ?? false;
                        });

                        const xmlIds = checkpoints.map((checkpoint) => checkpoint.xmlId);

                        if (hasCheckpointSelectionsFromXml) {
                            this.log.debug(
                                `FormEditEffects: autofillCheckpoints$: Found selections from XML and reminded user to manually review those.`,
                            );
                            // Disp lay message to the user and return actions to remove filters and to autofill
                            const modalInstance = this.modalService.show(YesNoModalDialogComponent, {
                                backdrop: 'static',
                                class: ModalClass.MODAL_MEDIUM,
                                initialState: {
                                    model: new YesNoModalDialogModel(
                                        `<string>${this.translate.instant('F4M_AUTOFILL_CHECKPOINTS_TITLE')}`,
                                        `<p><string>${this.translate.instant('F4M_AUTOFILL_CHECKPOINTS_MESSAGE')}<p>`,
                                        `<string>${this.translate.instant('F4M_AUTOFILL_UNDERSTOOD_LABEL')}`,
                                    ),
                                },
                                ariaLabelledBy: 'yes-no-modal-title',
                            });
                            const obs = modalInstance.content?.modalResult.asObservable() ?? of(true);
                            return obs.pipe(
                                mergeMap(() => [
                                    updateCheckpoints({
                                        xmlIds: xmlIds,
                                        selected: action.selected,
                                    }),
                                ]),
                            );
                        }

                        return [
                            updateCheckpoints({
                                xmlIds: xmlIds,
                                selected: action.selected,
                            }),
                        ];
                    }),
                    catchError((error) => {
                        this.log.error(`FormEditEffects: autofillCheckpoints$: error in pipeline: ${error}`);
                        return of(emptyAction());
                    }),
                ),
            ),
        ),
    );

    public autofillFunctionalElements$ = createEffect(() =>
        this.actions$.pipe(
            ofType(autofillFunctionalElements),
            concatMap((action) =>
                this.store.select(getFunctionalElementsToAutofillFromContentFactory({ contentId: action.id })).pipe(
                    take(1),
                    mergeMap((functionalElements) => {
                        // Autofill only when there was no selection for the checkpoint in the XML
                        const hasSelectionFromXML = functionalElements.some(
                            (functionalElement) => functionalElement.contentLoadedFromXml,
                        );

                        // The choices should not be set by the autofill functionality for functional elements of type value/dropdown-answers
                        const excludedFunctionalElementTypes = [
                            FUNCTIONAL_ELEMENT_TYPE.VALUE,
                            FUNCTIONAL_ELEMENT_TYPE.DROP_DOWN_OPTIONS,
                        ];

                        const contentIds = functionalElements
                            .filter(
                                (functionalElement) =>
                                    !excludedFunctionalElementTypes.includes(functionalElement.type) &&
                                    // we also need exclude date functional elements with function attribute defined
                                    // for these there are no choices displayed in the app.
                                    !(isDatePerformed(functionalElement) && isDefined(functionalElement.function)),
                            )
                            .map((functionalElement) => functionalElement.id);

                        if (hasSelectionFromXML) {
                            this.log.debug(
                                `FormEffects: autofillCheckpoints$: Found selections from XML and reminded user to manually review those.`,
                            );
                            // Disp lay message to the user and return actions to remove filters and to autofill
                            const modalInstance = this.modalService.show(YesNoModalDialogComponent, {
                                backdrop: 'static',
                                class: ModalClass.MODAL_MEDIUM,
                                initialState: {
                                    model: new YesNoModalDialogModel(
                                        `<string>${this.translate.instant('F4M_AUTOFILL_CHECKPOINTS_TITLE')}`,
                                        `<p><string>${this.translate.instant('F4M_AUTOFILL_CHECKPOINTS_MESSAGE')}<p>`,
                                        `<string>${this.translate.instant('F4M_AUTOFILL_UNDERSTOOD_LABEL')}`,
                                    ),
                                },
                                ariaLabelledBy: 'yes-no-modal-title',
                            });

                            const obs = modalInstance.content?.modalResult.asObservable() ?? of(true);
                            return obs.pipe(
                                map(() =>
                                    updateFunctionalElementsChoicesSelection({
                                        ids: contentIds,
                                        selected: action.selected,
                                    }),
                                ),
                            );
                        }

                        return of(
                            updateFunctionalElementsChoicesSelection({
                                ids: contentIds,
                                selected: action.selected,
                            }),
                        );
                    }),
                ),
            ),
        ),
    );

    public updateFunctionalElementsChoicesSelection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateFunctionalElementsChoicesSelection),
            withLatestFrom(
                this.store.select(getFormDoctype),
                this.store.select(getFormFunctionalElements),
                this.store.select(getFormType),
            ),
            switchMap(([action, doctype, functionalElements, formType]) =>
                this.store.select(getFormSelectedParticipantCopyId).pipe(
                    map((selectedParticipantId) => {
                        action.ids.forEach((contentId) => {
                            const functionalElement = functionalElements[contentId];
                            const choices = functionalElement.choices;
                            if (!isDefined(choices)) {
                                throw new Error(
                                    `FormEditEffects: updateFunctionalElementsChoicesSelection$: encountered functional element without choices!`,
                                );
                            }
                            const choiceToSelect = Object.values(choices).find((choice) => choice.selected);

                            if (functionalElement.xmlId) {
                                this.xmlSerializerService.updateFunctionalElementSelection(
                                    functionalElement.xmlId,
                                    choiceToSelect ? choiceToSelect.xmlId : undefined,
                                );
                            }

                            if (isDefined(choiceToSelect) && choiceToSelect.type === CHOICE_TYPE.CHOICE_NA) {
                                // remarks should be set to N.A for the remarks functional element.
                                if (
                                    isRemarksFunctionalElement(functionalElement) &&
                                    (functionalElement?.remarks?.length === 0 ||
                                        functionalElement.remarks === FormsConstants.NA) &&
                                    functionalElement.remarksXmlId
                                ) {
                                    this.xmlSerializerService.updateInputField(
                                        functionalElement.remarksXmlId,
                                        FormsConstants.NA,
                                        formType,
                                    );
                                }

                                if (isMaterialSerialNumber(functionalElement)) {
                                    this.xmlSerializerService.updateInputField(
                                        functionalElement.serialXmlId,
                                        functionalElement.serialValue.length === 0
                                            ? FormsConstants.NA
                                            : functionalElement.serialValue,
                                        formType,
                                    );

                                    this.xmlSerializerService.updateInputField(
                                        functionalElement.materialXmlId,
                                        functionalElement.materialValue.length === 0
                                            ? FormsConstants.NA
                                            : functionalElement.materialValue,
                                        formType,
                                    );

                                    this.xmlSerializerService.updateInputField(
                                        functionalElement.descriptionXmlId,
                                        functionalElement.descriptionValue.length === 0
                                            ? FormsConstants.NA
                                            : functionalElement.descriptionValue,
                                        formType,
                                    );
                                }

                                if (isDatePerformed(functionalElement) && functionalElement.dateXmlId) {
                                    this.xmlSerializerService.updateInputField(
                                        functionalElement.dateXmlId,
                                        FormsConstants.NA,
                                        formType,
                                    );
                                }

                                if (isValue(functionalElement) && functionalElement.valueXmlId) {
                                    this.xmlSerializerService.updateInputField(
                                        functionalElement.valueXmlId,
                                        FormsConstants.NA,
                                        formType,
                                    );
                                }
                            }
                        });
                        return updatedXml({
                            participant: selectedParticipantId,
                            xml: this.xmlParserService.getXml(doctype),
                        });
                    }),
                    catchError((error) => {
                        this.log.error(`FormEditEffects: updateCheckpoints$: error in pipeline: ${error}`);
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );

    public updatePopupFieldsVisibility$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateFunctionalElementSelection),
            switchMap((action) =>
                combineLatest([
                    this.store.select(getFormSelectedParticipantCopyId),
                    this.store.select(getFormFunctionalElements),
                    this.store.select(getFormDoctype),
                ]).pipe(
                    take(1),
                    map(([selectedParticipantId, functionalElements, doctype]) => {
                        const functionalElement = functionalElements[action.id];
                        const remarksHidden = FormUtilities.isRemarksFieldHidden(functionalElement);

                        this.xmlSerializerService.updateElementVisibility(
                            functionalElement.remarksXmlId,
                            !remarksHidden,
                        );
                        this.xmlSerializerService.updateDefectiveComponentReplacementPopupFieldsVisibility(
                            functionalElement,
                        );

                        return updatedXml({
                            participant: selectedParticipantId,
                            xml: this.xmlParserService.getXml(doctype),
                        });
                    }),
                    catchError((error) => {
                        this.log.error(
                            `FormEditEffects: updateRemarksFieldVisibility$: error in pipeline: ${JSON.stringify(
                                error,
                            )}`,
                        );
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );

    public updatePopupFieldsVisibilityForAutoFill$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateFunctionalElementsChoicesSelection),
            withLatestFrom(this.store.select(getFormDoctype), this.store.select(getFormFunctionalElements)),
            switchMap(([action, doctype, functionalElements]) =>
                this.store.select(getFormSelectedParticipantCopyId).pipe(
                    map((selectedParticipantId) => {
                        action.ids.forEach((contentId) => {
                            const functionalElement = functionalElements[contentId];

                            const hidden = FormUtilities.isRemarksFieldHidden(functionalElement);

                            this.xmlSerializerService.updateElementVisibility(functionalElement.remarksXmlId, !hidden);
                            this.xmlSerializerService.updateDefectiveComponentReplacementPopupFieldsVisibility(
                                functionalElement,
                            );
                        });
                        return updatedXml({
                            participant: selectedParticipantId,
                            xml: this.xmlParserService.getXml(doctype),
                        });
                    }),
                    catchError((error) => {
                        this.log.error(`FormEditEffects: updateRemarksFieldVisibility$: error in pipeline: ${error}`);
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );

    public updateAnswerFieldsVisibility$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateOptionSet),
            switchMap((action) =>
                combineLatest([
                    this.store.select(getFormSelectedParticipantCopyId),
                    this.store.select(getFormFunctionalElements),
                    this.store.select(getFormDoctype),
                ]).pipe(
                    take(1),
                    map(([selectedParticipantId, functionalElements, doctype]) => {
                        const functionalElement = functionalElements[action.id];

                        this.xmlSerializerService.updateElementVisibility(action.xmlId, action.newValue);
                        this.xmlSerializerService.updateDefectiveComponentReplacementPopupFieldsVisibility(
                            functionalElement,
                        );

                        return updatedXml({
                            participant: selectedParticipantId,
                            xml: this.xmlParserService.getXml(doctype),
                        });
                    }),
                    catchError((error) => {
                        this.log.error(
                            `FormEditEffects: updateRemarksFieldVisibility$: error in pipeline: ${JSON.stringify(
                                error,
                            )}`,
                        );
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );

    public updateDropDownOptionsElementsChoicesSelection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateDropDownOptions),
            withLatestFrom(this.store.select(getFormDoctype), this.store.select(getFormFunctionalElements)),
            switchMap(([action, doctype, functionalElements]) =>
                this.store.select(getFormSelectedParticipantCopyId).pipe(
                    map((selectedParticipantId) => {
                        const dropDownOptionFunctionalElement = functionalElements[
                            action.id
                        ] as DropDownOptionsFunctionalElement;
                        const choices = dropDownOptionFunctionalElement.choices;
                        if (!isDefined(choices)) {
                            throw new Error(
                                `FormEditEffects: updateDropDownOptionsElementsChoicesSelection$: encountered functional element without choices!`,
                            );
                        }
                        const selectedChoice = Object.values(choices).find((choice) => choice?.selected);

                        if (dropDownOptionFunctionalElement.xmlId) {
                            this.xmlSerializerService.updateFunctionalElementSelection(
                                dropDownOptionFunctionalElement.xmlId,
                                selectedChoice ? selectedChoice.xmlId : undefined,
                            );
                        }

                        if (dropDownOptionFunctionalElement.function === FunctionTypes.SINGLE_SELECT) {
                            dropDownOptionFunctionalElement.optionSet.map((groupOptionSet) => {
                                groupOptionSet.options.map((option) => {
                                    // set visibility attribute and selected attribute
                                    this.xmlSerializerService.updateElementVisibility(option.id, option.selected);
                                    this.xmlSerializerService.updateDropdownOptionSelectedAttribute(
                                        option.id,
                                        option.selected,
                                    );
                                });
                            });
                        } else {
                            this.xmlSerializerService.updateElementVisibility(action.xmlId, action.newValue);
                            this.xmlSerializerService.updateDropdownOptionSelectedAttribute(
                                action.xmlId,
                                action.newValue,
                            );
                        }

                        const remarksHidden = FormUtilities.isRemarksFieldHidden(dropDownOptionFunctionalElement);
                        this.xmlSerializerService.updateElementVisibility(
                            dropDownOptionFunctionalElement.remarksXmlId,
                            !remarksHidden,
                        );

                        return updatedXml({
                            participant: selectedParticipantId,
                            xml: this.xmlParserService.getXml(doctype),
                        });
                    }),
                    catchError((error) => {
                        this.log.error(
                            `FormEditEffects: updateDropDownOptionsElementsChoicesSelection$: error in pipeline: ${error}`,
                        );
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );

    public updateTopicVisibility$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateTopicVisibility),
            withLatestFrom(this.store.select(getFormDoctype), this.store.select(getFormSelectedParticipantCopyId)),
            switchMap(([action, doctype, selectedParticipantId]) =>
                this.store.select(getFormContent).pipe(
                    map((content) => {
                        const parentContent: ContentBlock = content[action.id];

                        if (!isDefined(parentContent)) {
                            throw new Error(`FormEditEffects: updateTopicVisibility$: encountered error!`);
                        }

                        const contentIds = new Set([
                            parentContent.id,
                            ...parentContent.children.map((child) => child.id),
                        ]);

                        for (const topicContent of Object.values(content)) {
                            if (contentIds.has(topicContent.id) && topicContent.xmlId !== undefined) {
                                this.xmlSerializerService.updateElementVisibility(
                                    topicContent.xmlId,
                                    action.visibility,
                                );
                            }
                        }

                        return updatedXml({
                            participant: selectedParticipantId,
                            xml: this.xmlParserService.getXml(doctype),
                        });
                    }),
                    catchError((error) => {
                        this.log.error(`FormEditEffects: updateTopicVisibility$: error in pipeline: ${error}`);
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );

    public hideOptionPresentElements$ = createEffect(() =>
        this.actions$.pipe(
            ofType(enableManualIndividualization),
            switchMap(() =>
                combineLatest([
                    this.store.select(getFormSelectedParticipantCopyId),
                    this.store.select(getFormDoctype),
                ]).pipe(
                    map(([selectedParticipantId, doctype]) => {
                        this.xmlSerializerService.hideOptionPresentElements();

                        return updatedXml({
                            participant: selectedParticipantId,
                            xml: this.xmlParserService.getXml(doctype),
                        });
                    }),
                    catchError((error) => {
                        this.log.error(
                            `FormEditEffects: hideOptionPresentElements$: error in pipeline: ${JSON.stringify(error)}`,
                        );
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );

    public updateMeasuringEquipmentRowVisibility$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateMeasurementEquipmentRowVisibility),
            withLatestFrom(
                this.store.select(getFormSelectedParticipantCopyId),
                this.store.select(getFormDoctype),
                this.store.select(getFormType),
                this.store.select(getMeasuringEquipments).pipe(distinctUntilChanged(), filter(isDefined)),
            ),
            switchMap(([action, selectedParticipantId, doctype, formType, measuringEquipments]) => {
                const isColumnWithoutValue = measuringEquipments?.rows.some(
                    (row) => row.visibility && row.columns.some((column) => column.value === ''),
                );
                this.xmlSerializerService.updateElementVisibility(action.rowId, action.visibility);
                if (!action.visibility) {
                    measuringEquipments?.rows.map((row) => {
                        if (row.rowId === action.rowId) {
                            row.columns.map((column) => {
                                this.xmlSerializerService.updateInputField(column.columnId, '', formType);
                            });
                        }
                    });
                }

                return [
                    updatedXml({
                        participant: selectedParticipantId,
                        xml: this.xmlParserService.getXml(doctype),
                    }),
                    updateMeasurementEquipmentSubmitSatus({ submitStatus: !isColumnWithoutValue }),
                ];
            }),
            catchError((error) => {
                this.log.error(
                    `FormEditEffects: updateMeasuringEquipmentVisibility$: error in pipeline: ${JSON.stringify(error)}`,
                );
                return EMPTY;
            }),
        ),
    );

    public hideMeasuringEquipmentRows$ = createEffect(() =>
        this.actions$.pipe(
            ofType(disableMeasurementEquipmentTable),
            withLatestFrom(
                this.store.select(getFormSelectedParticipantCopyId),
                this.store.select(getFormDoctype),
                this.store.select(getFormType),
                this.store.select(getMeasuringEquipments).pipe(distinctUntilChanged()),
            ),
            switchMap(([action, selectedParticipantId, doctype, formType, measuringEquipments]) => {
                measuringEquipments?.rows.map((row) => {
                    if (row.rowId !== action.rowId) {
                        this.xmlSerializerService.updateElementVisibility(row.rowId, false);
                    }
                    row.columns.map((column) => {
                        this.xmlSerializerService.updateInputField(column.columnId, '', formType);
                    });
                });

                return [
                    updatedXml({
                        participant: selectedParticipantId,
                        xml: this.xmlParserService.getXml(doctype),
                    }),
                    updateMeasurementEquipmentSubmitSatus({ submitStatus: action.noToolsUsed }),
                ];
            }),
            catchError((error) => {
                this.log.error(
                    `FormEditEffects: hideMeasuringEquipmentRows$: error in pipeline: ${JSON.stringify(error)}`,
                );
                return EMPTY;
            }),
        ),
    );

    public updateMeasuringEquipmentColumnData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateMeasurementEquipmentColumnData),
            withLatestFrom(
                this.store.select(getFormSelectedParticipantCopyId),
                this.store.select(getFormDoctype),
                this.store.select(getFormType),
                this.store.select(getMeasuringEquipments).pipe(distinctUntilChanged(), filter(isDefined)),
            ),
            switchMap(([action, selectedParticipantId, doctype, formType, measuringEquipments]) => {
                const isColumnWithoutValue = measuringEquipments?.rows.some(
                    (row) => row.visibility && row.columns.some((column) => column.value === ''),
                );

                measuringEquipments?.rows.map((row) => {
                    if (row.rowId === action.rowId) {
                        row.columns.map((column) => {
                            if (column.columnId === action.columnId) {
                                this.xmlSerializerService.updateInputField(action.columnId, action.value, formType);
                            }
                        });
                    }
                });

                const submitStatus = measuringEquipments.noMeasurementToolsUsed || !isColumnWithoutValue;

                return [
                    updatedXml({
                        participant: selectedParticipantId,
                        xml: this.xmlParserService.getXml(doctype),
                    }),
                    updateMeasurementEquipmentSubmitSatus({ submitStatus: submitStatus }),
                ];
            }),
            catchError((error) => {
                this.log.error(
                    `FormEditEffects: updateMeasuringEquipmentColumnData$: error in pipeline: ${JSON.stringify(error)}`,
                );
                return EMPTY;
            }),
        ),
    );

    public updateMeasuringEquipmentVisibility$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateMeasurementEquipmentsVisibility),
            switchMap((action) =>
                combineLatest([
                    this.store.select(getFormSelectedParticipantCopyId),
                    this.store.select(getFormDoctype),
                    this.store.select(getMeasuringEquipments).pipe(distinctUntilChanged()),
                ]).pipe(
                    map(([selectedParticipantId, doctype, measuringEquipments]) => {
                        this.xmlSerializerService.updateElementVisibility(
                            measuringEquipments?.xmlId,
                            action.visibility,
                        );

                        return updatedXml({
                            participant: selectedParticipantId,
                            xml: this.xmlParserService.getXml(doctype),
                        });
                    }),
                    catchError((error) => {
                        this.log.error(
                            `FormEditEffects: updateMeasuringEquipmentVisibility$: error in pipeline: ${JSON.stringify(
                                error,
                            )}`,
                        );
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );
}
